import React from "react";
import PrescribedTherapySelect from "./PrescribedTherapySelect";
import type { TherapyType } from "../api-client";

export interface TherapyTermFieldProps {
  /** Always "therapy" — the Rx forms' change handlers switch on it. */
  name?: string;
  value?: string;
  /** Event-shaped, so the forms' existing handleInputChange works untouched. */
  onChange: (event: { target: { name: string; value: string } }) => void;
  disabled?: boolean;
  therapyType?: TherapyType | string;
  isSupplementary?: boolean;
  style?: React.CSSProperties;
  /** Accepted and applied to the wrapper — several call sites size the cell this way. */
  sx?: Record<string, any>;
  /** Accepted and ignored: leftovers from the MUI field's prop surface. */
  id?: string | number;
  label?: string;
}

/**
 * Drop-in replacement for the `Controls.Select` that used to sit in the Rx
 * forms' Heilmittel cell.
 *
 * Exists only to absorb the impedance: the forms call
 * `handleInputChange(event, rowIndex, …)` and read `event.target.name/value`,
 * whereas `PrescribedTherapySelect` has a clean `onChange(value: string)` API.
 * Putting the adapter here keeps that DOM-event shape out of the new component
 * and keeps the 16 call-site edits mechanical.
 *
 * The `options` prop is gone rather than ignored: the old lists were
 * tarif-filtered billable line items, which is the wrong catalog level for a
 * prescription (MD-232). Options now come from the term catalog inside
 * PrescribedTherapySelect.
 */
export const TherapyTermField: React.FC<TherapyTermFieldProps> = ({
  name = "therapy",
  value,
  onChange,
  disabled,
  therapyType,
  isSupplementary,
  style,
  sx,
}) => (
  <div style={{ ...(sx as React.CSSProperties), ...style }}>
    <PrescribedTherapySelect
      value={value}
      onChange={(next) => onChange({ target: { name, value: next } })}
      therapyType={therapyType}
      isSupplementary={isSupplementary}
      disabled={disabled}
    />
  </div>
);

export default TherapyTermField;
